import React from "react";
import {
  Card as UICard,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import { Skeleton } from "@/components/Base/Skeleton";
import {
  BarChart3Icon,
  TrendingUpIcon,
  TrendingDownIcon,
  ActivityIcon,
} from "lucide-react";
import {
  ContainerTimeMetric,
  SerializedContainerTimeMetric,
} from "@/types/v2/PrecomputedMetrics";

interface MetricsStatisticsPanelProps {
  metric:
    | ContainerTimeMetric
    | SerializedContainerTimeMetric
    | null
    | undefined;
  isLoading: boolean;
  timeUnit: "minutes" | "hours" | "days";
}

export const MetricsStatisticsPanel: React.FC<MetricsStatisticsPanelProps> = ({
  metric,
  isLoading,
  timeUnit,
}) => {
  if (isLoading) {
    return (
      <UICard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3Icon className="h-5 w-5" />
            Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </UICard>
    );
  }

  if (!metric || !metric.statistics) {
    return (
      <UICard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3Icon className="h-5 w-5" />
            Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            No statistics available
          </div>
        </CardContent>
      </UICard>
    );
  }

  const { statistics } = metric;
  const range = statistics.maxTime - statistics.minTime;
  const standardDeviation = statistics.standardDeviation;

  const formatValue = (value: number) => {
    return value.toFixed(1);
  };

  const getUnitLabel = () => {
    switch (timeUnit) {
      case "minutes":
        return "min";
      case "hours":
        return "hrs";
      case "days":
        return "days";
      default:
        return "";
    }
  };

  return (
    <UICard>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3Icon className="h-5 w-5" />
          Statistics
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {/* Average */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center">
            <div className="mb-2 flex items-center justify-center gap-1">
              <ActivityIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Average</span>
            </div>
            <div className="text-2xl font-semibold text-blue-900">
              {formatValue(statistics.averageTime)}
            </div>
            <div className="text-xs text-blue-600">{getUnitLabel()}</div>
          </div>

          {/* Median */}
          <div className="bg-green-50 border-green-200 rounded-lg border p-4 text-center">
            <div className="mb-2 flex items-center justify-center gap-1">
              <BarChart3Icon className="text-green-600 h-4 w-4" />
              <span className="text-green-800 text-sm font-medium">Median</span>
            </div>
            <div className="text-green-900 text-2xl font-semibold">
              {formatValue(statistics.medianTime)}
            </div>
            <div className="text-green-600 text-xs">{getUnitLabel()}</div>
          </div>

          {/* Min */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-center">
            <div className="mb-2 flex items-center justify-center gap-1">
              <TrendingDownIcon className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">
                Minimum
              </span>
            </div>
            <div className="text-2xl font-semibold text-orange-900">
              {formatValue(statistics.minTime)}
            </div>
            <div className="text-xs text-orange-600">{getUnitLabel()}</div>
          </div>

          {/* Max */}
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 text-center">
            <div className="mb-2 flex items-center justify-center gap-1">
              <TrendingUpIcon className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">
                Maximum
              </span>
            </div>
            <div className="text-2xl font-semibold text-purple-900">
              {formatValue(statistics.maxTime)}
            </div>
            <div className="text-xs text-purple-600">{getUnitLabel()}</div>
          </div>
        </div>

        {/* Additional Statistics */}
        <div className="mt-6 border-t pt-6">
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
            <div className="text-center">
              <div className="font-semibold text-gray-900">
                {formatValue(range)}
              </div>
              <div className="text-gray-600">Range</div>
            </div>

            <div className="text-center">
              <div className="font-semibold text-gray-900">
                {formatValue(standardDeviation)}
              </div>
              <div className="text-gray-600">Std Dev</div>
            </div>

            <div className="text-center">
              <div className="font-semibold text-gray-900">
                {statistics.totalCards || metric.calculations.length}
              </div>
              <div className="text-gray-600">Data Points</div>
            </div>
          </div>
        </div>

        {/* Employee Breakdown Preview */}
        {metric.employeeBreakdown && metric.employeeBreakdown.length > 0 && (
          <div className="mt-6 border-t pt-6">
            <h4 className="mb-3 font-medium text-gray-900">Top Performers</h4>
            <div className="space-y-2">
              {metric.employeeBreakdown.slice(0, 3).map((employee) => (
                <div
                  key={employee.employeeId}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="font-medium">{employee.employeeName}</span>
                  <div className="text-right">
                    <div>
                      {formatValue(employee.averageTime)} {getUnitLabel()}
                    </div>
                    <div className="text-xs text-gray-500">
                      ({employee.cardCount} cards)
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </UICard>
  );
};
